import type { SimResult } from '../engine/types'
import type { NumberList } from './geometry'
import { linearScale } from './scale'

/**
 * The rules that decide what a fan chart *shows*, as opposed to how it is drawn.
 *
 * These three lived as private helpers inside the two things that render a fan —
 * `useAnimatedFan` owned the y-domain, `FanChart` owned the crossing marker and
 * the age ticks. That was correct while there was exactly one renderer. The share
 * card is a second one, and a second copy of `scaleFor` in particular would be a
 * silent divergence: the card's fan would be drawn to a different scale from the
 * app's and look like a different projection of the same plan.
 *
 * So they live here, deliberately free of React and of any DOM, which is also
 * what lets the canvas renderer import them.
 */

/**
 * Headroom above the top of the scale, so the median and the requirement line
 * never graze the top edge.
 */
export const Y_HEADROOM = 1.15

/**
 * The y-axis is scaled to the DECISION, not to the tallest thing in the
 * projection.
 *
 * Scaling to the overall maximum sounds like the neutral choice and is not.
 * Corpus grows lognormally, so the luckiest tenth of paths run away from the
 * median — over forty years of 80% equity the top of the fan reached ₹120 Cr
 * while the median sat at ₹3 Cr. The axis then had to reach ₹127 Cr, and the
 * FIRE requirement line — the single thing the chart exists to let you compare
 * against — rendered at 3.9% of the chart height, with the median at 2.5%.
 * Everything a reader actually looks at was crushed into the bottom sliver.
 *
 * So the scale comes from the target age: the 90th percentile THERE, or the
 * requirement at the end of the projection, whichever is higher. Past the target
 * the band simply leaves the top of the frame and is clipped. Nothing is
 * misrepresented — the fan that remains is drawn to the same scale as the axis
 * labels beside it — and the exact height of an outcome nobody plans around is
 * not information worth the readability of everything else.
 */
export function fanYMax(result: SimResult): number {
  // An empty projection would index p90[-1] and requiredMedian[-1], both
  // undefined, and Math.max(undefined, undefined, 1) is NaN — which then
  // propagates silently through linearScale into every plotted coordinate.
  if (result.bands.p90.length === 0 || result.requiredMedian.length === 0) {
    return Y_HEADROOM
  }
  const target = Math.min(Math.max(0, result.targetIndex), result.bands.p90.length - 1)
  const requirementAtEnd = result.requiredMedian[result.requiredMedian.length - 1]
  return Math.max(result.bands.p90[target], requirementAtEnd, 1) * Y_HEADROOM
}

/**
 * The y-domain for several fans sharing one chart.
 *
 * `fanYMax` is right for one fan and wrong for two, and the reason is worth
 * stating precisely because the obvious diagnosis is wrong. It is NOT the
 * requirement term: a first attempt moved that from the end of the projection to
 * the latest target and changed the axis by nothing at all, because in every
 * realistic pair the binding term is the LATER scenario's p90 at its own target.
 * Corpus grows lognormally, so seven more years of compounding dwarfs everything
 * the earlier scenario has, and taking a plain max crushes it into the floor.
 *
 * Measured, seed 0x5eed, 10,000 paths, as a percentage of plot height:
 *
 * ```
 * targets 50 vs 57      A req@50   A med@50   B med@57
 *   max(fanYMax, …)       17.5%      21.6%      48.4%
 *   this rule             31.5%      38.8%      87.0%
 * targets 50 vs 70
 *   max(fanYMax, …)        3.7%       4.6%      41.2%
 *   this rule              7.9%       9.7%      87.0%
 * ```
 *
 * So the rule asymmetrically privileges the scenario being read:
 *
 *  - the **active** layer keeps its whole band at its target, because that is the
 *    distribution you are actually looking at;
 *  - every **other** layer only has to keep its MEDIAN at its target on screen.
 *    Its band may leave the top of the frame — exactly the bargain `fanYMax`
 *    already strikes past a target, and those years are drawn faded anyway;
 *  - the requirement keeps `fanYMax`'s own term, read at the END of the
 *    projection. Moving it to the latest target was tried and is wrong twice
 *    over: it never binds in a realistic pair, so it fixed nothing, and where it
 *    DOES bind it lowers the axis below the requirement line's own right-hand
 *    end, pushing the goal off the top of the frame.
 *
 * **When the two targets coincide this is exactly `fanYMax`**, so the common
 * "what if I invest more" comparison does not move the axis at all, and adding a
 * second plan cannot silently rescale the first. A test pins that equality.
 */
export function fanYMaxAcross(results: SimResult[], activeIndex = 0): number {
  const usable = results.filter((r) => r.bands.p90.length > 0 && r.requiredMedian.length > 0)
  if (usable.length === 0) return Y_HEADROOM

  const clampIndex = (r: SimResult) =>
    Math.min(Math.max(0, r.targetIndex), r.bands.p90.length - 1)

  let peak = 1
  results.forEach((result, index) => {
    if (!usable.includes(result)) return
    const target = clampIndex(result)
    const band = index === activeIndex ? result.bands.p90[target] : result.bands.p50[target]
    const requirementAtEnd = result.requiredMedian[result.requiredMedian.length - 1]
    peak = Math.max(peak, band, requirementAtEnd)
  })
  return peak * Y_HEADROOM
}

/**
 * The x-domain for several fans sharing one chart — the union of their age axes.
 *
 * `FanChart` already took this union for the render, with a comment saying the
 * shared horizon makes the age arrays match anyway so the union merely "costs
 * nothing". Both halves of that are true and it is still load-bearing: on a drag
 * that re-simulates BOTH scenarios the two worker replies land in separate state
 * updates, so for a frame or two the axes genuinely differ in length, in start, or
 * in both.
 *
 * It lives here rather than inside the component because the render and the
 * hit-test have to agree on it and did not — see `scrubIndexAt`. A domain computed
 * in two places is the same silent-divergence risk this file was created for.
 */
export function fanXDomain(frames: readonly { ages: NumberList }[]): [number, number] {
  let min = Infinity
  let max = -Infinity
  for (const { ages } of frames) {
    if (ages.length === 0) continue
    if (ages[0] < min) min = ages[0]
    if (ages[ages.length - 1] > max) max = ages[ages.length - 1]
  }
  // Nothing drawable. `linearScale` pins a zero-width domain to the range start,
  // which is the honest answer when there is no axis to speak of.
  return min === Infinity ? [0, 0] : [min, max]
}

/**
 * Which sample the scrubber lands on for a pointer at `offsetX` inside the plot.
 *
 * Two different age axes meet here and mixing them up is the whole bug this
 * function exists to hold still:
 *
 *  - the **domain** must be the one the render drew with — the union across every
 *    layer. A hit-test that inverts a different scale from the one that drew the
 *    chart reports the wrong year for the pixel under the cursor, by up to six
 *    samples on a two-fan chart whose targets are seven years apart.
 *  - the **offset** subtracted afterwards must be the ACTIVE layer's own first
 *    age, because the returned index addresses that layer's `ages`. Subtracting
 *    the domain's minimum instead reads one year high on every sample whenever the
 *    active layer starts later than the union, and the top index then gets pinned
 *    silently by the clamp below.
 *
 * They are easy to conflate because they coincide whenever both layers start on
 * the same age, which is every settled frame and most transient ones.
 */
export function scrubIndexAt(
  offsetX: number,
  plotRange: readonly [number, number],
  frames: readonly { ages: NumberList }[],
  activeIndex: number,
): number {
  const ages = frames[activeIndex]?.ages
  if (ages === undefined || ages.length === 0) return 0

  const age = linearScale(fanXDomain(frames), plotRange).invert(offsetX)

  /* Nearest sample by its ACTUAL age, not `round(age - ages[0])`.
     That subtraction assumes exactly one sample per year. Every series this ships
     with satisfies it (`simulate.ts` writes `ages[y] = currentAge + y`), and it was
     fine while the maths lived inside the component — but `findCrossing` below
     refuses the same assumption in the same file, for the stated reason that it is
     "not a property a shared module should quietly depend on", and this module is
     imported by the canvas card renderer too. A scan of forty entries per pointer
     event costs nothing and removes the assumption instead of documenting it. */
  let best = 0
  let bestDistance = Infinity
  for (let i = 0; i < ages.length; i++) {
    const distance = Math.abs(ages[i] - age)
    if (distance < bestDistance) {
      bestDistance = distance
      best = i
    }
  }
  return best
}

/**
 * First age at which the median path clears its requirement, linearly
 * interpolated between the two bracketing years. Null if it never does.
 *
 * Takes the three series rather than a `FanFrame` so that both callers fit: the
 * app passes the mid-animation frame, the share card passes the settled
 * `SimResult` directly.
 */
export function findCrossing(
  ages: NumberList,
  p50: NumberList,
  required: NumberList,
): { age: number; value: number } | null {
  // Index 0 is a crossing too. Someone who already meets their requirement on
  // day one has not "crossed" at the next birthday — there is nothing to
  // interpolate, and the loop below would place the marker a whole sample late
  // (or, if the requirement rises faster than the median, exactly one sample
  // late). Both the app chart and the shared card draw this marker.
  if (p50.length > 0 && p50[0] >= required[0]) {
    return { age: ages[0], value: p50[0] }
  }

  for (let i = 1; i < p50.length; i++) {
    if (p50[i] >= required[i]) {
      const prevGap = p50[i - 1] - required[i - 1]
      const gap = p50[i] - required[i]
      const t = gap === prevGap ? 0 : prevGap / (prevGap - gap)
      const clamped = Math.max(0, Math.min(1, t))
      return {
        // Scaled by the actual gap between samples, not added raw. Adding the
        // fraction directly assumes one year per sample — true of every series
        // this ships with, and silently a year out on any other spacing, which
        // is not a property a shared module should quietly depend on.
        age: ages[i - 1] + clamped * (ages[i] - ages[i - 1]),
        value: p50[i - 1] + (p50[i] - p50[i - 1]) * clamped,
      }
    }
  }
  return null
}

/** Round ages at a readable spacing — every 5 or 10 years depending on span. */
export function ageTicks(ages: NumberList): number[] {
  if (ages.length === 0) return []
  const first = ages[0]
  const last = ages[ages.length - 1]
  const step = last - first > 26 ? 10 : 5

  const ticks: number[] = []
  for (let age = Math.ceil(first / step) * step; age <= last; age += step) ticks.push(age)
  // The starting age anchors the reader; keep it unless a tick already sits on top of it.
  if (ticks.length === 0 || ticks[0] - first > step / 2) ticks.unshift(first)
  return ticks
}
